import { t, TFunction } from 'i18next';
import {
    BatchReturnStatusType,
    ReturnItem,
    ReturnOrderLine,
    ReturnOrderLineType,
    ReturnSlip,
    ReturnStatusType,
} from 'returnCenter-api';
import { ExtendedReturnItem, LoadingCards } from 'store/reducers/shared/sliceTypes';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';

export const getStatusTypeString = (statusType?: number): keyof LoadingCards | undefined => {
    switch (statusType) {
        case ReturnStatusType.InProgress:
            return 'inprogress';
        case ReturnStatusType.OnHold:
            return 'onHold';
        case ReturnStatusType.Completed:
            return 'completed';
        case ReturnStatusType.Created:
            return 'created';
        case ReturnStatusType.ItemsSelected:
            return 'itemsSelected';
        case ReturnStatusType.IncomingPackage:
            return 'incomingPackage';
        case ReturnStatusType.Received:
            return 'received';
        case ReturnStatusType.CreditError:
            return 'creditError';
        case ReturnStatusType.Processing:
            return 'processing';
        case ReturnStatusType.CreditedBatchPending:
            return 'creditedBatchPending';
        case ReturnStatusType.BatchHandled:
            return 'batchHandled';
        default:
            return undefined;
    }
};

export const getStatsTranslations = (t: TFunction, type?: ReturnStatusType) => {
    switch (type) {
        case ReturnStatusType.Completed:
            return t('returnStatus.completedReturns');
        case ReturnStatusType.InProgress:
            return t('returnStatus.inProgress');
        case ReturnStatusType.OnHold:
            return t('returnStatus.onHold');
        case ReturnStatusType.Created:
            return t('returnStatus.createdReturns');
        case ReturnStatusType.ItemsSelected:
            return t('returnStatus.itemsSelected');
        case ReturnStatusType.IncomingPackage:
            return t('returnStatus.incomingPackage');
        case ReturnStatusType.Received:
            return t('returnStatus.receivedReturns');
        case ReturnStatusType.CreditError:
            return t('returnStatus.creditError');
        case ReturnStatusType.Processing:
            return t('returnStatus.processing', 'Processing');
        case ReturnStatusType.CreditedBatchPending:
            return t('returnStatus.creditedBatchPending');
        case ReturnStatusType.BatchHandled:
            return t('returnStatus.batchHandled', 'Batch handled');
        default:
            return t('returnStatus.unknown', 'Not registered');
    }
};

// ============ BATCH RETURN HELPERS ============

export const getBatchStatusTypeString = (statusType?: BatchReturnStatusType): string | undefined => {
    switch (statusType) {
        case BatchReturnStatusType.Open:
            return 'open';
        case BatchReturnStatusType.Closed:
            return 'closed';
        case BatchReturnStatusType.Sent:
            return 'sent';
        case BatchReturnStatusType.PartiallyReceived:
            return 'partiallyReceived';
        case BatchReturnStatusType.Received:
            return 'received';
        case BatchReturnStatusType.PartiallyProcessed:
            return 'partiallyProcessed';
        case BatchReturnStatusType.Completed:
            return 'completed';
        case BatchReturnStatusType.Incoming:
            return 'incoming';
        default:
            return undefined;
    }
};

export const parseBatchStatusFromUrl = (str?: string): BatchReturnStatusType | undefined => {
    switch (str) {
        case 'open':
            return BatchReturnStatusType.Open;
        case 'closed':
            return BatchReturnStatusType.Closed;
        case 'sent':
            return BatchReturnStatusType.Sent;
        case 'partiallyReceived':
            return BatchReturnStatusType.PartiallyReceived;
        case 'received':
            return BatchReturnStatusType.Received;
        case 'partiallyProcessed':
            return BatchReturnStatusType.PartiallyProcessed;
        case 'completed':
            return BatchReturnStatusType.Completed;
        case 'incoming':
            return BatchReturnStatusType.Incoming;
        default:
            return undefined;
    }
};

export const getBatchStatsTranslations = (t: TFunction, type?: BatchReturnStatusType) => {
    switch (type) {
        case BatchReturnStatusType.Open:
            return t('batchReturnStatus.open', 'Open');
        case BatchReturnStatusType.Closed:
            return t('batchReturnStatus.closed', 'Closed');
        case BatchReturnStatusType.Sent:
            return t('batchReturnStatus.sent', 'Sent');
        case BatchReturnStatusType.PartiallyReceived:
            return t('batchReturnStatus.partiallyReceived', 'Partially received');
        case BatchReturnStatusType.Received:
            return t('batchReturnStatus.received', 'Received');
        case BatchReturnStatusType.PartiallyProcessed:
            return t('batchReturnStatus.partiallyProcessed', 'Partially processed');
        case BatchReturnStatusType.Completed:
            return t('batchReturnStatus.completed', 'Completed');
        case BatchReturnStatusType.Incoming:
            return t('batchReturnStatus.incoming', 'Incoming');
        default:
            return t('batchReturnStatus.unknown', 'Unknown');
    }
};

export const getBatchStatusInfo = (
    id: number | string | undefined,
    t: TFunction,
): { type: BatchReturnStatusType | undefined; heading: string } => {
    switch (id) {
        case 'open':
        case BatchReturnStatusType.Open:
            return { type: BatchReturnStatusType.Open, heading: t('batchReturnStatus.open', 'Open') };
        case 'closed':
        case BatchReturnStatusType.Closed:
            return { type: BatchReturnStatusType.Closed, heading: t('batchReturnStatus.closed', 'Closed') };
        case 'sent':
        case BatchReturnStatusType.Sent:
            return { type: BatchReturnStatusType.Sent, heading: t('batchReturnStatus.sent', 'Sent') };
        case 'partiallyReceived':
        case BatchReturnStatusType.PartiallyReceived:
            return {
                type: BatchReturnStatusType.PartiallyReceived,
                heading: t('batchReturnStatus.partiallyReceived', 'Partially received'),
            };
        case 'received':
        case BatchReturnStatusType.Received:
            return { type: BatchReturnStatusType.Received, heading: t('batchReturnStatus.received', 'Received') };
        case 'partiallyProcessed':
        case BatchReturnStatusType.PartiallyProcessed:
            return {
                type: BatchReturnStatusType.PartiallyProcessed,
                heading: t('batchReturnStatus.partiallyProcessed', 'Partially processed'),
            };
        case 'completed':
        case BatchReturnStatusType.Completed:
            return { type: BatchReturnStatusType.Completed, heading: t('batchReturnStatus.completed', 'Completed') };
        case 'incoming':
        case BatchReturnStatusType.Incoming:
            return { type: BatchReturnStatusType.Incoming, heading: t('batchReturnStatus.incoming', 'Incoming') };
        default:
            return { type: undefined, heading: t('error_occurred') };
    }
};

export const hasSplits = (item: ReturnOrderLine) => {
    return item.returnItems && item.returnItems.length > 1;
};

export const alignFlexStart = (item: ReturnItem) => (hasSplits(item) ? ('start' as const) : undefined);

export function validateReturnSelection(
    isReturnItemSelected: Record<string, boolean>,
    allItemsHaveReasonCodes: boolean,
    t: TFunction,
    toast: typeof import('react-toastify').toast,
): boolean {
    // Find the first order with no selected item
    const missingOrder = Object.entries(isReturnItemSelected).find(([, selected]) => !selected);

    if (missingOrder) {
        const [orderNumber] = missingOrder;
        toast.error(t('selectAtLeastOneItem', { orderNumber }), {
            autoClose: 3500,
            closeOnClick: true,
        });
        return false;
    }

    if (!allItemsHaveReasonCodes) {
        toast.error(t('allItemsMustHaveReasonCodes'), {
            autoClose: 3500,
            closeOnClick: true,
        });
        return false;
    }

    return true;
}

export const getOrderNumbers = (orders: string[]): { key: string; value: string } => {
    if (orders.length === 0) {
        return { key: 'noOrdersAvailable', value: '' };
    }

    return orders.length > 1 ? { key: 'orders', value: orders.join(', ') } : { key: 'order', value: orders[0] };
};

export const canSplitItem = (item: ReturnOrderLine): boolean => {
    if (!item.quantityShipped || !item.quantityAvailableForReturn) return false;
    return item.quantityAvailableForReturn > 1;
};

export const getCellPadding = (idx: number): string | undefined => (idx === 0 ? '0' : undefined);

/**
 * Whether a scanned EAN/SKU (lower-cased) selects the line. A mis-pick line never matches on EAN, and matches on
 * its own SKU only when that differs from the parent's.
 */
export const scanMatchesLine = (
    line: ReturnOrderLine,
    siblingLines: ReturnOrderLine[] | null | undefined,
    lowerKey: string,
): boolean => {
    const sku = line.sku?.trim().toLowerCase();
    if (line.type !== ReturnOrderLineType.MisPick) {
        return line.ean?.trim().toLowerCase() === lowerKey || sku === lowerKey;
    }
    const parentSku = findMisPickParent(line, siblingLines)?.sku?.trim().toLowerCase();
    return !!sku && sku !== parentSku && sku === lowerKey;
};

const quantityInReturn = (line: ReturnOrderLine | undefined) =>
    (line?.returnItems ?? []).reduce((sum, item) => sum + (item.quantityInReturn ?? 0), 0);

/** The ordered line a mis-pick line was delivered in place of, among the lines of its order group. */
export const findMisPickParent = (line: ReturnOrderLine, groupLines: ReturnOrderLine[] | null | undefined) =>
    groupLines?.find(
        (ol) => ol.type === ReturnOrderLineType.Normal && ol.orderLineNumber === line.misPickParentOrderLineNumber,
    );

/**
 * Whether a mis-pick line is returned while the ordered line it replaces is not. Saving drops a line with nothing in
 * the return, so the mis-pick line would lose the line it is priced and credited against.
 */
export const misPickParentLacksQuantity = (line: ReturnOrderLine, groupLines: ReturnOrderLine[] | null | undefined) =>
    line.type === ReturnOrderLineType.MisPick &&
    quantityInReturn(line) > 0 &&
    quantityInReturn(findMisPickParent(line, groupLines)) <= 0;

/** The first mis-pick line on the slip whose ordered line has nothing in the return, with that line if present. */
export const findMisPickLineWithoutParentQuantity = (slip: ReturnSlip | null | undefined) => {
    for (const group of slip?.returnItems ?? []) {
        for (const line of group.orderLines ?? []) {
            if (misPickParentLacksQuantity(line, group.orderLines)) {
                return { line, parent: findMisPickParent(line, group.orderLines) };
            }
        }
    }
    return undefined;
};

export const canIncrementByScan = (
    returnSlip: ReturnSlip | null | undefined,
    scanKey: string,
): { canIncrement: true } | { canIncrement: false; sku: string; max: number } => {
    if (!returnSlip?.returnItems) return { canIncrement: false, sku: '', max: 0 };

    const lowerKey = scanKey.toLowerCase();
    let lastMatchSku = '';
    let lastMatchMax = 0;

    for (const orderGroup of returnSlip.returnItems) {
        for (const orderLine of orderGroup.orderLines ?? []) {
            if (!scanMatchesLine(orderLine, orderGroup.orderLines, lowerKey)) continue;

            lastMatchSku = orderLine.sku ?? '';
            lastMatchMax = orderLine.quantityAvailableForReturn ?? 0;

            const usedQty = (orderLine.returnItems ?? []).reduce((sum, ri) => sum + (ri.quantityInReturn ?? 0), 0);
            if (usedQty < lastMatchMax) {
                return { canIncrement: true };
            }
        }
    }

    return { canIncrement: false, sku: lastMatchSku, max: lastMatchMax };
};

export const getStatusInfo = (
    id: number | string | undefined,
    t: TFunction,
): { type: ReturnStatusType | undefined; heading: string } => {
    switch (id) {
        case 'created':
        case ReturnStatusType.Created:
            return { type: ReturnStatusType.Created, heading: t('createdReturns') };
        case 'itemsSelected':
        case ReturnStatusType.ItemsSelected:
            return { type: ReturnStatusType.ItemsSelected, heading: t('itemsSelectedReturns') };
        case 'incomingPackage':
        case ReturnStatusType.IncomingPackage:
            return { type: ReturnStatusType.IncomingPackage, heading: t('incomingPackageReturns') };
        case 'received':
        case ReturnStatusType.Received:
            return { type: ReturnStatusType.Received, heading: t('receivedReturns') };
        case 'inprogress':
        case ReturnStatusType.InProgress:
            return { type: ReturnStatusType.InProgress, heading: t('inProgress') };
        case 'onHold':
        case ReturnStatusType.OnHold:
            return { type: ReturnStatusType.OnHold, heading: t('onHold') };
        case 'completed':
        case ReturnStatusType.Completed:
            return { type: ReturnStatusType.Completed, heading: t('completedReturns') };
        case 'creditError':
        case ReturnStatusType.CreditError:
            return { type: ReturnStatusType.CreditError, heading: t('creditError') };
        case 'creditedBatchPending':
        case ReturnStatusType.CreditedBatchPending:
            return { type: ReturnStatusType.CreditedBatchPending, heading: t('creditedBatchPending') };
        case 'batchHandled':
        case ReturnStatusType.BatchHandled:
            return { type: ReturnStatusType.BatchHandled, heading: t('batchHandled', 'Batch handled') };
        default:
            return { type: undefined, heading: t('error_occurred') };
    }
};

export const getReturnItemId = (item: ExtendedReturnItem): number | string => item.id ?? item.splitId ?? '';

// Utility functions
export function populateMissingLpns(returnSlip: ReturnSlip): ReturnSlip {
    if (returnSlip.id == null || !returnSlip.returnItems) return returnSlip;

    const generatedLpn = `RETURN-${returnSlip.id}`;

    return {
        ...returnSlip,
        returnItems: returnSlip.returnItems.map((group) => ({
            ...group,
            orderLines: group.orderLines?.map((line) => {
                // A mis-pick line is goods received like any other, so it needs a pallet too.
                if (line.type !== ReturnOrderLineType.Normal && line.type !== ReturnOrderLineType.MisPick) return line;

                return {
                    ...line,
                    returnItems: line.returnItems?.map((item) => ({
                        ...item,
                        lpn: item.lpn?.trim() ? item.lpn : generatedLpn,
                    })),
                };
            }),
        })),
    };
}

export function canAddSplit(
    orderLine: ReturnOrderLine,
    _itemId: string | number,
    splitId: string | number,
    newSplitQty: number = 1,
): boolean {
    const totalQtyInReturnItems = orderLine.returnItems?.reduce((sum, item) => {
        if ((item as ExtendedReturnItem).splitId === splitId || item.id === splitId) {
            return sum + newSplitQty;
        }
        return sum + (item.quantityInReturn || 0);
    }, 0);

    if (orderLine.quantityAvailableForReturn && totalQtyInReturnItems !== undefined) {
        return totalQtyInReturnItems < orderLine.quantityAvailableForReturn;
    }
    return false;
}

/**
 * The order line number is not unique within a group: a batch receiver's slip has one line per
 * row in the sender's package, so a line the sender split arrives as several lines with the same
 * number. Given the item, the line holding it is picked; otherwise the first with the number.
 */
export function findOrderLine(
    returnSlip: ReturnSlip | null,
    orderNumber: string,
    orderLineNumber: string,
    sourceReturnId?: number | null,
    itemId?: number | string,
): ReturnOrderLine | undefined {
    const orderGroup = returnSlip?.returnItems?.find(
        (group) =>
            group.orderNumber === orderNumber &&
            (sourceReturnId === undefined || group.sourceReturnId === sourceReturnId),
    );
    const lines = orderGroup?.orderLines?.filter((line) => line.orderLineNumber === orderLineNumber) ?? [];
    if (itemId !== undefined && lines.length > 1) {
        const holdingLine = lines.find((line) =>
            line.returnItems?.some(
                (item: ExtendedReturnItem) => item.id === itemId || item.splitId === itemId,
            ),
        );
        if (holdingLine) return holdingLine;
    }
    return lines[0];
}

export function findSplitItem(
    returnSlip: ReturnSlip | null,
    orderNumber: string,
    orderLineNumber: string,
    splitId: string | number,
    sourceReturnId?: number | null,
): ExtendedReturnItem | undefined {
    const orderLine = findOrderLine(returnSlip, orderNumber, orderLineNumber, sourceReturnId, splitId);
    return orderLine?.returnItems?.find((item: ExtendedReturnItem) =>
        item.splitId ? item.splitId === splitId : item.id === splitId,
    );
}

/**
 * The synthetic order line number for a new mis-pick line under the given parent: `MP-{parent}-{n}` with the
 * lowest free `n >= 1` in the group. It only has to be unique within the group, which is where
 * `findOrderLine` looks rows up.
 */
export function nextMisPickLineNumber(
    group: { orderLines?: ReturnOrderLine[] | null } | null | undefined,
    parentOrderLineNumber: string,
): string {
    const taken = new Set((group?.orderLines ?? []).map((line) => line.orderLineNumber));
    let n = 1;
    while (taken.has(`MP-${parentOrderLineNumber}-${n}`)) n++;
    return `MP-${parentOrderLineNumber}-${n}`;
}

/** Whether any row of the line has a reason code that is marked as a mis-pick. */
export function hasMisPickReasonCode(line: ReturnOrderLine, misPickReasonCodeIds: ReadonlySet<number>): boolean {
    return (line.returnItems ?? []).some((ri) => ri.reasonCode != null && misPickReasonCodeIds.has(ri.reasonCode));
}

/**
 * Orders the lines so that each mis-pick line follows its parent. A line whose parent is not in the list keeps
 * its place at the end, so nothing the server returned is hidden.
 */
export function orderMisPickLinesAfterParent(lines: ReturnOrderLine[]): ReturnOrderLine[] {
    const misPicks = lines.filter((line) => line.type === ReturnOrderLineType.MisPick);
    if (misPicks.length === 0) return lines;

    const placed = new Set<ReturnOrderLine>();
    const result: ReturnOrderLine[] = [];
    lines.forEach((line) => {
        if (line.type === ReturnOrderLineType.MisPick) return;
        result.push(line);
        if (line.type !== ReturnOrderLineType.Normal) return;
        misPicks
            .filter((mp) => mp.misPickParentOrderLineNumber === line.orderLineNumber && !placed.has(mp))
            .forEach((mp) => {
                placed.add(mp);
                result.push(mp);
            });
    });
    misPicks.filter((mp) => !placed.has(mp)).forEach((mp) => result.push(mp));
    return result;
}

export function handleApiError({ error, rejectWithValue }: { error: AxiosError; rejectWithValue: Function }) {
    const status = error?.response?.status;
    if (status === 404) {
        toast.error(t('error.returnSlipNotFound'), { autoClose: 3500, closeOnClick: true });
        return rejectWithValue({ message: t('error.returnSlipNotFound'), status: 404 });
    }
    if (status === 500) {
        toast.error(t('error.internalServerError'), { autoClose: 3500, closeOnClick: true });
        return rejectWithValue({ message: t('error.internalServerError'), status: 500 });
    }
    toast.error(t('error.unknownError'), { autoClose: 3500, closeOnClick: true });
    return rejectWithValue({ message: t('error.unknownError') });
}
